import * as React from 'react';

import TextInput from '@packages/systems/design-system/TextInput';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {
  Link,
  Note,
  NoteActions,
  NoteBody,
  View,
  InfoIcon,
  NoteLink,
} from '@packages/systems/spring';
import {TypeImplementation, withSystem} from '@packages/systems/wfdl';
import {EText, TText} from '@packages/systems/wfdl-ast';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';

import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';

const CUSTOM_ELEMENT_INFO_NOTE = 'CUSTOM_ELEMENT_INFO_NOTE';
const selectHasDismissedNote = (state: any) =>
  state.DesignerStore.permanentlyDismissedNotes.has(CUSTOM_ELEMENT_INFO_NOTE);

function useDismissCustomElementNote() {
  const fluxDispatch = useDispatch();

  const onDismiss = React.useCallback(() => {
    fluxDispatch({
      type: 'NOTE_DISMISSED',
      payload: {id: CUSTOM_ELEMENT_INFO_NOTE},
    });
  }, [fluxDispatch]);

  const hasDismissed = useDesignerSelector(selectHasDismissedNote);
  return [hasDismissed, onDismiss];
}

// Validates HTML tag names according to HTML specifications.
// 1. Standard HTML Elements: Tag names are composed of ASCII alphabetic characters.
//    Specification: https://html.spec.whatwg.org/multipage/syntax.html#tag-name
// 2. Custom Elements: Must contain a hyphen and cannot start with a number.
//    Specification: https://html.spec.whatwg.org/multipage/custom-elements.html#valid-custom-element-name
// The regex ensures that the tag starts with an alphabetic character,
// followed by zero or more alphanumeric characters or hyphens.
export const TAG_VALIDATION_REGEX = /^[a-zA-Z][a-zA-Z0-9-]*$/;
const validationRegex = new RegExp(TAG_VALIDATION_REGEX);

export const TagType = TypeImplementation.Alias({
  type: withSystem({label: 'Tag', isBindable: true}, TText),
  system: {
    defaultValue: 'div',
  },
  render: React.memo(function TagTypeText({expr, dispatch}) {
    const [hasDismissed, onDismiss] = useDismissCustomElementNote();
    const [invalid, setInvalid] = React.useState(false);
    const text = ContextualExpressions.getText(stripAnn(expr));
    const [value, setValue] = React.useState(text !== null ? text : '');

    React.useEffect(() => {
      const newText = ContextualExpressions.getText(stripAnn(expr));
      setValue(newText || '');
      setInvalid(false);
    }, [expr]);

    const onFocus = React.useCallback(() => {
      setInvalid(false);
    }, []);

    const onBlur = React.useCallback(() => {
      if (!validationRegex.test(value)) {
        // Reset
        setValue(text ?? '');
        return;
      }

      dispatch(updateExpressionAction(EText(value)));
    }, [value, dispatch, text]);

    const onChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
      },
      []
    );

    const onKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          if (validationRegex.test(value)) {
            dispatch(updateExpressionAction(EText(value)));
          } else {
            setInvalid(true);
          }
        }
      },
      [dispatch, value]
    );

    return (
      <View>
        <Hitarea>
          <TextInput
            type="text"
            data-automation-id="tag-type-input-field"
            placeholder="div"
            value={value}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            onChange={onChange}
            invalid={invalid}
          />
        </Hitarea>

        {hasDismissed ? null : (
          <View paddingTop="8px">
            <Note colorVariant="primary" icon={<InfoIcon />}>
              <NoteBody>
                A custom element adheres to the{' '}
                <Link
                  href="https://html.spec.whatwg.org/multipage/dom.html#elements"
                  target="_blank"
                  rel="noopener noreferrer"
                  shouldShowUnderlineOnHover
                >
                  HTML specification
                </Link>
                . Invalid tags and improper nesting of non-container elements
                can lead to rendering problems.
              </NoteBody>
              <NoteActions>
                <NoteLink style={{cursor: 'pointer'}} onClick={onDismiss}>
                  Ok, got it
                </NoteLink>
              </NoteActions>
            </Note>
          </View>
        )}
      </View>
    );
  }),
});
